import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X, Check, Package, Layers, FolderOpen, Monitor, ImageIcon } from 'lucide-react';

const PricingSettingsNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services');
  
  // Services state
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    category_id: '',
    internal_cost: 0,
    min_users: 1,
    included_options: [],
    extra_options: [],
    is_active: true
  });
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    contract_template_id: ''
  });
  
  // Contract templates state
  const [contractTemplates, setContractTemplates] = useState([]);
  
  // Options state
  const [options, setOptions] = useState([]);
  const [editingOption, setEditingOption] = useState(null);
  const [optionForm, setOptionForm] = useState({
    name: '',
    cost: 0,
    category_id: '',
    is_active: true
  });
  
  // Hardware state
  const [hardware, setHardware] = useState([]);
  const [editingHardware, setEditingHardware] = useState(null);
  const [hardwareForm, setHardwareForm] = useState({
    name: '',
    description: '',
    category: 'computers',
    image_url: '',
    base_price: 0,
    internal_cost: 0,
    specifications: {},
    stock_quantity: null,
    sku: '',
    is_active: true
  });
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [servicesRes, categoriesRes, optionsRes, hardwareRes, templatesRes] = await Promise.all([
        api.get('/pricing/services'),
        api.get('/pricing/categories'),
        api.get('/pricing/options'),
        api.get('/pricing/hardware'),
        api.get('/contract-templates')
      ]);
      setServices(servicesRes.data.services || []);
      setCategories(categoriesRes.data.categories || []);
      setOptions(optionsRes.data.options || []);
      setHardware(hardwareRes.data.products || []);
      setContractTemplates(templatesRes.data.templates || []);
    } catch (error) {
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  // ===== CATEGORIES =====
  const handleCreateCategory = () => {
    setEditingCategory('new');
    setCategoryForm({ name: '', description: '', contract_template_id: '' });
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setCategoryForm({
      name: category.name,
      description: category.description || ''
    });
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory === 'new') {
        await api.post('/pricing/categories', categoryForm);
        toast.success('Category created successfully');
      } else {
        await api.put(`/pricing/categories/${editingCategory}`, categoryForm);
        toast.success('Category updated successfully');
      }
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
      fetchAll();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/pricing/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // ===== OPTIONS =====
  const handleCreateOption = () => {
    setEditingOption('new');
    setOptionForm({
      name: '',
      cost: 0,
      category_id: categories[0]?.id || '',
      is_active: true
    });
  };

  const handleEditOption = (option) => {
    setEditingOption(option.id);
    setOptionForm({
      name: option.name,
      cost: option.cost,
      category_id: option.category_id,
      is_active: option.is_active
    });
  };

  const handleSaveOption = async () => {
    try {
      if (editingOption === 'new') {
        await api.post('/pricing/options', optionForm);
        toast.success('Option created successfully');
      } else {
        await api.put(`/pricing/options/${editingOption}`, optionForm);
        toast.success('Option updated successfully');
      }
      setEditingOption(null);
      setOptionForm({ name: '', cost: 0, category_id: '', is_active: true });
      fetchAll();
    } catch (error) {
      toast.error('Failed to save option');
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Are you sure you want to delete this option?')) return;
    try {
      await api.delete(`/pricing/options/${optionId}`);
      toast.success('Option deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete option');
    }
  };

  // ===== SERVICES =====
  const handleCreateService = () => {
    setEditingService('new');
    setServiceForm({
      name: '',
      description: '',
      category_id: categories[0]?.id || '',
      internal_cost: 0,
      min_users: 1,
      included_options: [],
      extra_options: [],
      is_active: true
    });
  };

  const handleEditService = (service) => {
    setEditingService(service.id);
    setServiceForm({
      name: service.name,
      description: service.description || '',
      category_id: service.category_id || '',
      internal_cost: service.internal_cost || 0,
      min_users: service.min_users,
      included_options: service.included_options || [],
      extra_options: service.extra_options || [],
      is_active: service.is_active
    });
  };

  const handleSaveService = async () => {
    try {
      if (editingService === 'new') {
        await api.post('/pricing/services', serviceForm);
        toast.success('Service created successfully');
      } else {
        await api.put(`/pricing/services/${editingService}`, serviceForm);
        toast.success('Service updated successfully');
      }
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        category_id: '',
        internal_cost: 0,
        min_users: 1,
        included_options: [],
        extra_options: [],
        is_active: true
      });
      fetchAll();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await api.delete(`/pricing/services/${serviceId}`);
      toast.success('Service deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const toggleOptionIncluded = (optionId) => {
    setServiceForm(prev => {
      const isCurrentlyIncluded = prev.included_options.includes(optionId);
      const isCurrentlyExtra = prev.extra_options.includes(optionId);
      
      if (isCurrentlyIncluded) {
        // Remove from included
        return {
          ...prev,
          included_options: prev.included_options.filter(id => id !== optionId)
        };
      } else {
        // Add to included, remove from extra if present
        return {
          ...prev,
          included_options: [...prev.included_options, optionId],
          extra_options: isCurrentlyExtra ? prev.extra_options.filter(id => id !== optionId) : prev.extra_options
        };
      }
    });
  };

  const toggleOptionExtra = (optionId) => {
    setServiceForm(prev => {
      const isCurrentlyIncluded = prev.included_options.includes(optionId);
      const isCurrentlyExtra = prev.extra_options.includes(optionId);
      
      if (isCurrentlyExtra) {
        // Remove from extra
        return {
          ...prev,
          extra_options: prev.extra_options.filter(id => id !== optionId)
        };
      } else {
        // Add to extra, remove from included if present
        return {
          ...prev,
          extra_options: [...prev.extra_options, optionId],
          included_options: isCurrentlyIncluded ? prev.included_options.filter(id => id !== optionId) : prev.included_options
        };
      }
    });
  };

  // Calculate base price from included options only
  const calculateBasePrice = (includedOptions) => {
    return includedOptions.reduce((total, optionId) => {
      const option = options.find(o => o.id === optionId);
      return total + (option?.cost || 0);
    }, 0);
  };

  // Calculate extras price
  const calculateExtrasPrice = (extraOptions) => {
    return extraOptions.reduce((total, optionId) => {
      const option = options.find(o => o.id === optionId);
      return total + (option?.cost || 0);
    }, 0);
  };

  const getFilteredOptions = (categoryId) => {
    if (!categoryId) return [];
    return options.filter(opt => opt.category_id === categoryId && opt.is_active);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  // ===== HARDWARE HANDLERS =====
  const handleCreateHardware = () => {
    setEditingHardware('new');
    setHardwareForm({
      name: '',
      description: '',
      category: 'computers',
      image_url: '',
      base_price: 0,
      internal_cost: 0,
      specifications: {},
      stock_quantity: null,
      sku: '',
      is_active: true
    });
  };

  const handleEditHardware = (product) => {
    setEditingHardware(product.id);
    setHardwareForm({
      name: product.name,
      description: product.description || '',
      category: product.category || 'computers',
      image_url: product.image_url || '',
      base_price: product.base_price,
      internal_cost: product.internal_cost || 0,
      specifications: product.specifications || {},
      stock_quantity: product.stock_quantity,
      sku: product.sku || '',
      is_active: product.is_active
    });
  };

  const handleSaveHardware = async () => {
    try {
      if (editingHardware === 'new') {
        await api.post('/pricing/hardware', hardwareForm);
        toast.success('Hardware product created successfully');
      } else {
        await api.put(`/pricing/hardware/${editingHardware}`, hardwareForm);
        toast.success('Hardware product updated successfully');
      }
      setEditingHardware(null);
      setHardwareForm({
        name: '',
        description: '',
        category: 'computers',
        image_url: '',
        base_price: 0,
        internal_cost: 0,
        specifications: {},
        stock_quantity: null,
        sku: '',
        is_active: true
      });
      fetchAll();
    } catch (error) {
      toast.error('Failed to save hardware product');
    }
  };

  const handleDeleteHardware = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this hardware product?')) return;
    try {
      await api.delete(`/pricing/hardware/${productId}`);
      toast.success('Hardware product deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete hardware product');
    }
  };

  const addSpecification = () => {
    if (!newSpecKey.trim() || !newSpecValue.trim()) {
      toast.error('Please enter both key and value for specification');
      return;
    }
    setHardwareForm(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [newSpecKey]: newSpecValue
      }
    }));
    setNewSpecKey('');
    setNewSpecValue('');
  };

  const removeSpecification = (key) => {
    setHardwareForm(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return {
        ...prev,
        specifications: newSpecs
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/settings')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Pricing & Quoting</h1>
                <p className="text-sm text-slate-500">Manage services, options, and categories</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex space-x-2 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('services')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
              activeTab === 'services'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            <Package className="w-4 h-4" />
            <span>Services</span>
          </button>
          <button
            onClick={() => setActiveTab('options')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
              activeTab === 'options'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            <Layers className="w-4 h-4" />
            <span>Options Library</span>
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
              activeTab === 'categories'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            <span>Categories</span>
          </button>
          <button
            onClick={() => setActiveTab('hardware')}
            className={`px-4 py-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
              activeTab === 'hardware'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            <Monitor className="w-4 h-4" />
            <span>Hardware</span>
          </button>
        </div>

        {/* CATEGORIES TAB */}
        {activeTab === 'categories' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-slate-800">Service Categories</h2>
              <Button onClick={handleCreateCategory} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>

            {editingCategory && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {editingCategory === 'new' ? 'New Category' : 'Edit Category'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="e.g., Remote Monitoring"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      placeholder="Brief description of this category"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Contract Template</Label>
                    <select
                      value={categoryForm.contract_template_id}
                      onChange={(e) => setCategoryForm({ ...categoryForm, contract_template_id: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No template (optional)</option>
                      {contractTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      This template will be used when generating contracts for quotes containing services in this category
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleSaveCategory} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button onClick={() => setEditingCategory(null)} variant="outline">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4">
              {categories.map((category) => (
                <Card key={category.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800">{category.name}</h3>
                        {category.description && (
                          <p className="text-sm text-slate-600 mt-1">{category.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          <p className="text-xs text-slate-500">
                            {options.filter(o => o.category_id === category.id).length} options
                          </p>
                          {category.contract_template_id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              📄 {contractTemplates.find(t => t.id === category.contract_template_id)?.name || 'Contract Template'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {categories.length === 0 && !editingCategory && (
              <Card>
                <CardContent className="p-8 text-center">
                  <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No categories yet</p>
                  <p className="text-sm text-slate-500">Create a category to organize your services</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* OPTIONS TAB */}
        {activeTab === 'options' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Options Library</h2>
                <p className="text-sm text-slate-500">Reusable service add-ons and features</p>
              </div>
              <Button onClick={handleCreateOption} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Option
              </Button>
            </div>

            {editingOption && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {editingOption === 'new' ? 'New Option' : 'Edit Option'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={optionForm.name}
                      onChange={(e) => setOptionForm({ ...optionForm, name: e.target.value })}
                      placeholder="e.g., DNS Filter, Antivirus, Remote Control"
                    />
                  </div>
                  <div>
                    <Label>Cost per User/Month (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={optionForm.cost}
                      onChange={(e) => setOptionForm({ ...optionForm, cost: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select
                      value={optionForm.category_id}
                      onChange={(e) => setOptionForm({ ...optionForm, category_id: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md"
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={optionForm.is_active}
                      onChange={(e) => setOptionForm({ ...optionForm, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <Label>Active</Label>
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleSaveOption} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button onClick={() => setEditingOption(null)} variant="outline">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4">
              {categories.map((category) => {
                const categoryOptions = options.filter(o => o.category_id === category.id);
                if (categoryOptions.length === 0) return null;

                return (
                  <div key={category.id}>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">{category.name}</h3>
                    <div className="grid gap-2">
                      {categoryOptions.map((option) => (
                        <Card key={option.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h4 className="font-semibold text-slate-800">{option.name}</h4>
                                  {!option.is_active && (
                                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-emerald-600 font-medium mt-1">
                                  £{option.cost.toFixed(2)}/user/month
                                </p>
                              </div>
                              <div className="flex space-x-2 ml-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditOption(option)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteOption(option.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {options.length === 0 && !editingOption && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Layers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No options yet</p>
                  <p className="text-sm text-slate-500">Create reusable options to build your services</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* SERVICES TAB */}
        {activeTab === 'services' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Services</h2>
                <p className="text-sm text-slate-500">Build services from your options library</p>
              </div>
              <Button onClick={handleCreateService} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </div>

            {editingService && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {editingService === 'new' ? 'New Service' : 'Edit Service'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Service Name</Label>
                    <Input
                      value={serviceForm.name}
                      onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                      placeholder="e.g., Basic RMM Package"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={serviceForm.description}
                      onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                      placeholder="Service description for quotes"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select
                      value={serviceForm.category_id}
                      onChange={(e) => setServiceForm({ ...serviceForm, category_id: e.target.value, enabled_options: [] })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md"
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {serviceForm.category_id && (
                    <div>
                      <Label>Service Options</Label>
                      <p className="text-xs text-slate-500 mb-2">Select whether each option is included in base price or available as an extra</p>
                      <div className="mt-2 space-y-2 border border-slate-200 rounded-md p-3 bg-white max-h-60 overflow-y-auto">
                        {getFilteredOptions(serviceForm.category_id).map((option) => {
                          const isIncluded = serviceForm.included_options.includes(option.id);
                          const isExtra = serviceForm.extra_options.includes(option.id);
                          
                          return (
                            <div key={option.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded border border-slate-100">
                              <div className="flex-1">
                                <p className="font-medium text-sm text-slate-800">{option.name}</p>
                                <p className="text-xs text-emerald-600">£{option.cost.toFixed(2)}/user/month</p>
                              </div>
                              <div className="flex items-center space-x-4">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isIncluded}
                                    onChange={() => toggleOptionIncluded(option.id)}
                                    className="rounded text-blue-600"
                                  />
                                  <span className="text-xs font-medium text-slate-700">Included</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isExtra}
                                    onChange={() => toggleOptionExtra(option.id)}
                                    className="rounded text-amber-600"
                                  />
                                  <span className="text-xs font-medium text-slate-700">Extra</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                        {getFilteredOptions(serviceForm.category_id).length === 0 && (
                          <p className="text-sm text-slate-500 text-center py-4">
                            No options available for this category. Create options first.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-slate-100 border border-slate-200 rounded-md p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Base Price (Included):</span>
                      <span className="text-lg font-bold text-blue-600">
                        £{calculateBasePrice(serviceForm.included_options).toFixed(2)}/user/month
                      </span>
                    </div>
                    {serviceForm.extra_options.length > 0 && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">Extras Available:</span>
                          <span className="text-lg font-bold text-amber-600">
                            +£{calculateExtrasPrice(serviceForm.extra_options).toFixed(2)}/user/month
                          </span>
                        </div>
                        <div className="pt-2 border-t border-slate-300 flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">Total with All Extras:</span>
                          <span className="text-xl font-bold text-emerald-600">
                            £{(calculateBasePrice(serviceForm.included_options) + calculateExtrasPrice(serviceForm.extra_options)).toFixed(2)}/user/month
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <Label>Internal Cost (MSP-only) (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={serviceForm.internal_cost}
                      onChange={(e) => setServiceForm({ ...serviceForm, internal_cost: parseFloat(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-slate-500 mt-1">Not visible to end users</p>
                  </div>

                  <div>
                    <Label>Minimum Users</Label>
                    <Input
                      type="number"
                      value={serviceForm.min_users}
                      onChange={(e) => setServiceForm({ ...serviceForm, min_users: parseInt(e.target.value) || 1 })}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={serviceForm.is_active}
                      onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <Label>Active</Label>
                  </div>

                  <div className="flex space-x-2">
                    <Button onClick={handleSaveService} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save Service
                    </Button>
                    <Button onClick={() => setEditingService(null)} variant="outline">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4">
              {services.map((service) => {
                const includedOptions = options.filter(o => service.included_options?.includes(o.id));
                const extraOptions = options.filter(o => service.extra_options?.includes(o.id));
                const profitMargin = service.base_price - (service.internal_cost || 0);

                return (
                  <Card key={service.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-slate-800">{service.name}</h3>
                            {!service.is_active && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                          {service.description && (
                            <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                          )}
                          {service.category_id && (
                            <p className="text-xs text-slate-500 mt-1">
                              Category: {getCategoryName(service.category_id)}
                            </p>
                          )}

                          <div className="mt-3 flex items-center space-x-4">
                            <div>
                              <p className="text-xs text-slate-500">Price</p>
                              <p className="text-lg font-bold text-emerald-600">
                                £{(service.base_price || 0).toFixed(2)}/user
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Cost (MSP-only)</p>
                              <p className="text-sm font-medium text-slate-700">
                                £{(service.internal_cost || 0).toFixed(2)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Profit</p>
                              <p className={`text-sm font-medium ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                £{profitMargin.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {(includedOptions.length > 0 || extraOptions.length > 0) && (
                            <div className="mt-3 space-y-2">
                              {includedOptions.length > 0 && (
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Included in Base Price:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {includedOptions.map((opt) => (
                                      <span key={opt.id} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded flex items-center space-x-1">
                                        <Check className="w-3 h-3" />
                                        <span>{opt.name}</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {extraOptions.length > 0 && (
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Available as Extras:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {extraOptions.map((opt) => (
                                      <span key={opt.id} className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded flex items-center space-x-1">
                                        <span>+</span>
                                        <span>{opt.name} (£{opt.cost.toFixed(2)})</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditService(service)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteService(service.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {services.length === 0 && !editingService && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No services yet</p>
                  <p className="text-sm text-slate-500">Create services by combining options from your library</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* HARDWARE TAB */}
        {activeTab === 'hardware' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Hardware Products</h2>
                <p className="text-sm text-slate-500">Manage physical products like computers, monitors, phones</p>
              </div>
              <Button onClick={handleCreateHardware} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Hardware
              </Button>
            </div>

            {editingHardware && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {editingHardware === 'new' ? 'New Hardware Product' : 'Edit Hardware Product'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Product Name</Label>
                      <Input
                        value={hardwareForm.name}
                        onChange={(e) => setHardwareForm({ ...hardwareForm, name: e.target.value })}
                        placeholder="e.g., Dell Monitor 24 inch"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <select
                        value={hardwareForm.category}
                        onChange={(e) => setHardwareForm({ ...hardwareForm, category: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md"
                      >
                        <option value="computers">Computers</option>
                        <option value="monitors">Monitors</option>
                        <option value="phones">Phones</option>
                        <option value="networking">Networking</option>
                        <option value="accessories">Accessories</option>
                        <option value="general">General</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={hardwareForm.description}
                      onChange={(e) => setHardwareForm({ ...hardwareForm, description: e.target.value })}
                      placeholder="Product description"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={hardwareForm.image_url}
                      onChange={(e) => setHardwareForm({ ...hardwareForm, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                    />
                    {hardwareForm.image_url && (
                      <img 
                        src={hardwareForm.image_url} 
                        alt="Preview" 
                        className="mt-2 h-24 w-24 object-cover rounded border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={hardwareForm.base_price}
                        onChange={(e) => setHardwareForm({ ...hardwareForm, base_price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Internal Cost (MSP-only) (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={hardwareForm.internal_cost}
                        onChange={(e) => setHardwareForm({ ...hardwareForm, internal_cost: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>SKU (Optional)</Label>
                      <Input
                        value={hardwareForm.sku}
                        onChange={(e) => setHardwareForm({ ...hardwareForm, sku: e.target.value })}
                        placeholder="e.g., MON-DEL-24"
                      />
                    </div>
                    <div>
                      <Label>Stock Quantity (Optional)</Label>
                      <Input
                        type="number"
                        value={hardwareForm.stock_quantity || ''}
                        onChange={(e) => setHardwareForm({ ...hardwareForm, stock_quantity: parseInt(e.target.value) || null })}
                        placeholder="Leave blank for unlimited"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Specifications</Label>
                    <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2">
                      {Object.entries(hardwareForm.specifications).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                          <div>
                            <span className="font-medium text-sm text-slate-700">{key}:</span>
                            <span className="text-sm text-slate-600 ml-2">{value}</span>
                          </div>
                          <Button
                            onClick={() => removeSpecification(key)}
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      
                      <div className="flex space-x-2 pt-2 border-t">
                        <Input
                          value={newSpecKey}
                          onChange={(e) => setNewSpecKey(e.target.value)}
                          placeholder="Key (e.g., RAM)"
                          className="flex-1"
                        />
                        <Input
                          value={newSpecValue}
                          onChange={(e) => setNewSpecValue(e.target.value)}
                          placeholder="Value (e.g., 16GB)"
                          className="flex-1"
                        />
                        <Button onClick={addSpecification} size="sm" variant="outline">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={hardwareForm.is_active}
                      onChange={(e) => setHardwareForm({ ...hardwareForm, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <Label>Active</Label>
                  </div>

                  <div className="flex space-x-2">
                    <Button onClick={handleSaveHardware} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save Hardware
                    </Button>
                    <Button onClick={() => setEditingHardware(null)} variant="outline">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4">
              {hardware.map((product) => {
                const profitMargin = product.base_price - (product.internal_cost || 0);
                return (
                  <Card key={product.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-4">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-20 h-20 object-cover rounded border"
                            onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23ddd" width="80" height="80"/%3E%3C/svg%3E'}
                          />
                        ) : (
                          <div className="w-20 h-20 bg-slate-100 rounded border flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-slate-800">{product.name}</h3>
                            {!product.is_active && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                          {product.description && (
                            <p className="text-sm text-slate-600 mt-1">{product.description}</p>
                          )}
                          {product.sku && (
                            <p className="text-xs text-slate-500 mt-1">SKU: {product.sku}</p>
                          )}

                          <div className="mt-3 flex items-center space-x-4">
                            <div>
                              <p className="text-xs text-slate-500">Price</p>
                              <p className="text-lg font-bold text-emerald-600">
                                £{product.base_price.toFixed(2)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Cost (MSP)</p>
                              <p className="text-sm font-medium text-slate-700">
                                £{(product.internal_cost || 0).toFixed(2)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Profit</p>
                              <p className={`text-sm font-medium ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                £{profitMargin.toFixed(2)}
                              </p>
                            </div>
                            {product.stock_quantity !== null && (
                              <div>
                                <p className="text-xs text-slate-500">Stock</p>
                                <p className="text-sm font-medium text-slate-700">{product.stock_quantity}</p>
                              </div>
                            )}
                          </div>

                          {Object.keys(product.specifications || {}).length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-slate-500 mb-1">Specifications:</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(product.specifications).map(([key, value]) => (
                                  <span key={key} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                    {key}: {value}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditHardware(product)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteHardware(product.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {hardware.length === 0 && !editingHardware && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Monitor className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No hardware products yet</p>
                  <p className="text-sm text-slate-500">Add computers, monitors, phones, and other hardware items</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingSettingsNew;
